import {
  CARD_COLORS,
  isWildCard,
  type Card,
  type CardColor,
  type PlayerView,
  type RoomView,
} from '@uno/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCountdown, useT } from '../hooks';
import { fmt, plural, type Translation } from '../i18n';
import { net, type FeedEvent } from '../net';
import type { CardTheme } from '../theme/types';
import { CardFace } from './CardFace';
import { PlayLog } from './PlayLog';

const LS_SORT = 'uno:sortHand';

/** Wilds last; within a colour, numbers ascending then Skip, Reverse, +2. */
const KIND_RANK: Record<string, number> = { number: 0, skip: 1, reverse: 2, draw2: 3, wild: 4, wild4: 5 };

/**
 * Groups the hand by colour for readability. Purely presentational — the server
 * addresses cards by id, so re-ordering the fan changes nothing it cares about.
 */
function byColour(a: Card, b: Card): number {
  const colourRank = (c: Card) => (isWildCard(c) ? CARD_COLORS.length : CARD_COLORS.indexOf(c.color as never));
  const byColourRank = colourRank(a) - colourRank(b);
  if (byColourRank !== 0) return byColourRank;
  const byKind = (KIND_RANK[a.kind] ?? 9) - (KIND_RANK[b.kind] ?? 9);
  if (byKind !== 0) return byKind;
  return (a.value ?? 0) - (b.value ?? 0);
}

/** Returns a copy without `key`. */
function omit<T extends Record<string, unknown>>(obj: T, key: string): T {
  const { [key]: _drop, ...rest } = obj;
  return rest as T;
}

interface Props {
  room: RoomView;
  theme: CardTheme;
  pending: boolean;
  feed: FeedEvent[];
}

export function GameTable({ room, theme, pending, feed }: Props) {
  const t = useT();
  const self = room.self;
  const me = room.players.find((p) => p.id === self?.id);
  const [wildCard, setWildCard] = useState<Card | null>(null);
  const [penaltyFlash, setPenaltyFlash] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [sorted, setSorted] = useState(() => {
    try {
      return localStorage.getItem(LS_SORT) === '1';
    } catch {
      return false;
    }
  });
  const [bubbles, setBubbles] = useState<Record<string, { id: number; text: string }>>({});

  const game = room.game;
  const secondsLeft = useCountdown(game.turnDeadline);
  const isMyTurn = self?.isMyTurn ?? false;
  const locked = pending || !self;

  // Seat the local player at the bottom and fan the rest around the table.
  const opponents = useMemo(() => {
    if (!self) return room.players;
    const idx = room.players.findIndex((p) => p.id === self.id);
    if (idx < 0) return room.players;
    return [...room.players.slice(idx + 1), ...room.players.slice(0, idx)];
  }, [room.players, self?.id]);

  // Flash the table red when we personally eat a penalty.
  const lastFeedId = useRef(0);
  useEffect(() => {
    const fresh = feed.filter((f) => f.id > lastFeedId.current);
    if (fresh.length) lastFeedId.current = fresh[fresh.length - 1].id;
    const hitMe = fresh.some(
      (f) =>
        f.event.type === 'drew' &&
        f.event.playerId === self?.id &&
        f.event.reason !== 'turn',
    );
    if (hitMe) {
      setPenaltyFlash(true);
      const t = window.setTimeout(() => setPenaltyFlash(false), 600);
      return () => window.clearTimeout(t);
    }
  }, [feed, self?.id]);

  /**
   * Playing never declares UNO on the player's behalf.
   *
   * Calling it is the player's job — either pre-emptively with the UNO button
   * while still holding two cards, or in the window that opens once they are
   * down to one. Auto-declaring here would make the player permanently safe and
   * reduce both the UNO and JUDGE buttons to decoration.
   */
  // Chat rides the event feed, so a bubble is just the newest `chat` event for
  // each player, held briefly. One source of truth with the log.
  const lastChatId = useRef(0);
  useEffect(() => {
    const fresh = feed.filter((f) => f.id > lastChatId.current && f.event.type === 'chat');
    if (!fresh.length) return;
    lastChatId.current = fresh[fresh.length - 1].id;

    const added = fresh.map((f) => {
      const e = f.event as Extract<typeof f.event, { type: 'chat' }>;
      return { playerId: e.playerId, id: f.id, text: e.text };
    });
    setBubbles((prev) => {
      const next = { ...prev };
      for (const a of added) next[a.playerId] = { id: a.id, text: a.text };
      return next;
    });

    const timers = added.map((a) =>
      window.setTimeout(() => {
        setBubbles((prev) => (prev[a.playerId]?.id === a.id ? omit(prev, a.playerId) : prev));
      }, 7000),
    );
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [feed]);

  const play = (card: Card) => {
    if (card.color === 'wild') {
      setWildCard(card);
      return;
    }
    net.act({ type: 'play', cardId: card.id });
  };

  const playWild = (color: CardColor) => {
    if (!wildCard) return;
    net.act({ type: 'play', cardId: wildCard.id, chosenColor: color });
    setWildCard(null);
  };

  const judgeTarget = self?.judgeablePlayerIds[0] ?? null;

  const hand = useMemo(() => {
    const cards = self?.hand ?? [];
    return sorted ? [...cards].sort(byColour) : cards;
  }, [self?.hand, sorted]);

  const toggleSort = () => {
    setSorted((on) => {
      try {
        localStorage.setItem(LS_SORT, on ? '0' : '1');
      } catch {
        /* private mode — the preference just won't persist */
      }
      return !on;
    });
  };

  return (
    <div
      className={`table table--${game.activeColor ?? 'none'} ${penaltyFlash ? 'table--penalty' : ''} ${
        isMyTurn ? 'table--myturn' : ''
      }`}
      style={{ '--turn-total': `${room.rules.turnSeconds}s` } as React.CSSProperties}
    >
      <button
        type="button"
        className={`logtoggle ${showLog ? 'logtoggle--on' : ''}`}
        onClick={() => setShowLog((v) => !v)}
        aria-pressed={showLog}
      >
        {t.log.title}
      </button>
      {showLog && <PlayLog room={room} feed={feed} onClose={() => setShowLog(false)} />}

      <section className="table__opponents">
        {opponents.map((p) => (
          <OpponentSeat
            key={p.id}
            player={p}
            theme={theme}
            isTurn={game.turnPlayerId === p.id}
            secondsLeft={game.turnPlayerId === p.id ? secondsLeft : null}
            judgeable={self?.judgeablePlayerIds.includes(p.id) ?? false}
            onJudge={() => net.act({ type: 'judge', targetId: p.id })}
            disabled={locked}
            bubble={bubbles[p.id]?.text}
            t={t}
          />
        ))}
      </section>

      <section className="table__center">
        <div className="pile pile--draw">
          <button
            type="button"
            className="pile__stack"
            disabled={locked || !isMyTurn || !!self?.drawnCardId}
            onClick={() => net.act({ type: 'draw' })}
            aria-label={
              game.pendingDraw > 0 ? fmt(t.game.drawNCards, { count: game.pendingDraw }) : t.game.drawACard
            }
          >
            <CardFace theme={theme} faceDown />
            {game.pendingDraw > 0 && <span className="pile__pending">+{game.pendingDraw}</span>}
          </button>
          <span className="pile__label">{fmt(t.game.drawPileLeft, { count: game.drawPileCount })}</span>
        </div>

        <div className="table__status">
          <DirectionDial direction={game.direction} t={t} />
          <div className={`colorchip colorchip--${game.activeColor ?? 'none'}`} title={t.game.activeColour}>
            {game.activeColor ?? '—'}
          </div>
          {secondsLeft !== null && (
            <div className={`timer ${secondsLeft <= 5 ? 'timer--urgent' : ''}`}>{secondsLeft}s</div>
          )}
        </div>

        <div className="pile pile--discard">
          {game.topCard ? (
            <CardFace
              key={game.topCard.id}
              card={game.topCard}
              theme={theme}
              className="card--dropped"
            />
          ) : (
            <div className="pile__empty" />
          )}
          <span className="pile__label">{t.game.discard}</span>
        </div>
      </section>

      <TurnBanner room={room} secondsLeft={secondsLeft} t={t} />

      <section className={`table__me ${isMyTurn ? 'table__me--turn' : ''}`}>
        {bubbles[self?.id ?? '']?.text && (
          <div className="bubble bubble--mine">{bubbles[self!.id].text}</div>
        )}

        <div className="myinfo">
          <span className="myinfo__name">
            {me?.nickname ?? 'You'}
            {me?.isHost && <span className="tag tag--host">{t.common.host}</span>}
          </span>
          <span className="myinfo__right">
            <button
              type="button"
              className={`sortbtn ${sorted ? 'sortbtn--on' : ''}`}
              onClick={toggleSort}
              aria-pressed={sorted}
              title={sorted ? t.game.sortAsDealt : t.game.sortByColour}
            >
              🎨
            </button>
            <span className="muted">{plural(t.game.cardCount, self?.hand.length ?? 0)}</span>
          </span>
        </div>

        <Hand
          cards={hand}
          theme={theme}
          playableIds={self?.playableCardIds ?? []}
          drawnCardId={self?.drawnCardId ?? null}
          disabled={locked}
          onPlay={play}
          emptyLabel={t.game.noCards}
        />

        <div className="actionbar">
          <button
            type="button"
            className={`btn btn--uno ${self?.canCallUno ? 'btn--pulse' : ''}`}
            disabled={locked || !self?.canCallUno}
            onClick={() => net.act({ type: 'call_uno' })}
          >
            {t.game.uno}
          </button>

          <button
            type="button"
            className={`btn btn--judge ${judgeTarget ? 'btn--pulse' : ''}`}
            disabled={locked || !judgeTarget}
            onClick={() => judgeTarget && net.act({ type: 'judge', targetId: judgeTarget })}
          >
            {t.game.judge}
          </button>

          {self?.drawnCardId ? (
            <button type="button" className="btn" disabled={locked} onClick={() => net.act({ type: 'pass' })}>
              {t.game.pass}
            </button>
          ) : (
            <button
              type="button"
              className="btn"
              disabled={locked || !isMyTurn}
              onClick={() => net.act({ type: 'draw' })}
            >
              {game.pendingDraw > 0 ? fmt(t.game.takeCards, { count: game.pendingDraw }) : t.game.draw}
            </button>
          )}
        </div>
      </section>

      {self?.canChallengeWildFour && (
        <ChallengePrompt
          accuserName={room.players.find((p) => p.id === game.challenge?.targetId)?.nickname ?? t.events.someone}
          pendingDraw={game.pendingDraw}
          disabled={locked}
          t={t}
        />
      )}

      {wildCard && <ColorPicker onPick={playWild} onCancel={() => setWildCard(null)} t={t} />}

      {game.phase === 'GameOver' && <GameOverPanel room={room} t={t} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function OpponentSeat({
  player,
  theme,
  isTurn,
  secondsLeft,
  judgeable,
  onJudge,
  disabled,
  bubble,
  t,
}: {
  player: PlayerView;
  theme: CardTheme;
  isTurn: boolean;
  secondsLeft: number | null;
  judgeable: boolean;
  onJudge: () => void;
  disabled: boolean;
  /** Their most recent chat message, while it is still fresh. */
  bubble?: string;
  t: Translation;
}) {
  const out = player.status === 'forfeited' || player.status === 'finished';
  return (
    <div className={`seat ${isTurn ? 'seat--turn' : ''} ${out ? 'seat--out' : ''}`}>
      {bubble && <div className="bubble">{bubble}</div>}
      <div className="seat__head">
        <span
          className={`avatar ${player.isBot ? 'avatar--bot' : player.emoji ? 'avatar--emoji' : ''}`}
          aria-hidden="true"
        >
          {player.isBot ? '🤖' : player.emoji || player.nickname.slice(0, 1).toUpperCase()}
        </span>
        <span className="seat__name">
          {player.nickname}
          {player.isBot && <span className="tag tag--bot">{t.common.ai}</span>}
          {player.isHost && <span className="tag tag--host">{t.common.host}</span>}
        </span>
        {secondsLeft !== null && <span className="seat__timer">{secondsLeft}s</span>}
      </div>

      <div className="seat__cards" aria-label={plural(t.game.cardCount, player.handCount)}>
        {Array.from({ length: Math.min(player.handCount, 7) }, (_, i) => (
          <CardFace key={i} theme={theme} faceDown className="minicard" style={{ '--i': i } as React.CSSProperties} />
        ))}
        <span className="seat__count">{player.handCount}</span>
      </div>

      <div className="seat__tags">
        {player.status === 'disconnected' && (
          <span className="tag tag--warn">
            {player.reconnectMsLeft
              ? fmt(t.game.offlineFor, { seconds: Math.ceil(player.reconnectMsLeft / 1000) })
              : t.room.offlineTag}
          </span>
        )}
        {player.status === 'forfeited' && <span className="tag tag--warn">{t.game.forfeitedTag}</span>}
        {player.place && <span className="tag tag--good">{fmt(t.game.placeTag, { place: player.place })}</span>}
        {player.handCount === 1 && (
          <span className={`tag ${player.calledUno ? 'tag--good' : 'tag--danger'}`}>
            {player.calledUno ? t.game.unoTag : t.game.oneCard}
          </span>
        )}
      </div>

      {judgeable && (
        <button type="button" className="seat__judge btn--pulse" disabled={disabled} onClick={onJudge}>
          {t.game.judge}
        </button>
      )}
    </div>
  );
}

function Hand({
  cards,
  theme,
  playableIds,
  drawnCardId,
  disabled,
  onPlay,
  emptyLabel,
}: {
  cards: Card[];
  theme: CardTheme;
  playableIds: string[];
  drawnCardId: string | null;
  disabled: boolean;
  onPlay: (card: Card) => void;
  emptyLabel: string;
}) {
  const playable = new Set(playableIds);
  const ref = useRef<HTMLDivElement>(null);

  // A freshly drawn card can land off the end of a scrolled fan, where the
  // player never sees that it is theirs to play. Bring it into view.
  useEffect(() => {
    if (!drawnCardId) return;
    ref.current
      ?.querySelector(`[data-card-id="${drawnCardId}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [drawnCardId]);

  return (
    <div className="hand" ref={ref} style={{ '--n': cards.length } as React.CSSProperties}>
      {cards.map((card, i) => {
        const canPlay = playable.has(card.id) && !disabled;
        return (
          <CardFace
            key={card.id}
            card={card}
            theme={theme}
            playable={canPlay}
            dimmed={!canPlay}
            selected={card.id === drawnCardId}
            interactive
            onClick={canPlay ? () => onPlay(card) : undefined}
            className="hand__card"
            style={{ '--i': i } as React.CSSProperties}
          />
        );
      })}
      {cards.length === 0 && <p className="muted">{emptyLabel}</p>}
    </div>
  );
}

function TurnBanner({ room, secondsLeft, t }: { room: RoomView; secondsLeft: number | null; t: Translation }) {
  const game = room.game;
  const turnName = room.players.find((p) => p.id === game.turnPlayerId)?.nickname;
  const mine = room.self?.isMyTurn;

  let text: string;
  if (game.phase === 'GameOver') text = t.game.gameOver;
  else if (game.phase === 'AwaitingChallenge') text = t.game.challengeWindow;
  else if (mine) {
    text = game.pendingDraw > 0 ? fmt(t.game.yourTurnStack, { count: game.pendingDraw }) : t.game.yourTurn;
  } else text = turnName ? fmt(t.game.othersTurn, { name: turnName }) : t.game.waiting;

  return (
    <div className={`turnbanner ${mine ? 'turnbanner--mine' : ''}`} role="status">
      <span>{text}</span>
      {secondsLeft !== null && secondsLeft <= 10 && <span className="turnbanner__timer">{secondsLeft}</span>}
    </div>
  );
}

function DirectionDial({ direction, t }: { direction: 1 | -1; t: Translation }) {
  return (
    <div
      className={`dial ${direction === 1 ? 'dial--cw' : 'dial--ccw'}`}
      title={direction === 1 ? t.game.clockwise : t.game.counterClockwise}
    >
      {/*
        Centre (24,24), r=15. The arc runs 270 degrees clockwise from 12 o'clock
        to 9 o'clock, where travel is straight up — so the head sits exactly on
        the arc's end with its arms trailing behind it. `dial--ccw` mirrors the
        whole thing, which puts the head at 3 o'clock, still pointing up.
      */}
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <path
          d="M24 9A15 15 0 1 1 9 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <path
          d="M4.8 29.2L9 24L13.2 29.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function ColorPicker({ onPick, onCancel, t }: { onPick: (color: CardColor) => void; onCancel: () => void; t: Translation }) {
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal modal--sm colorpicker" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal__title">{t.game.pickColour}</h2>
        <div className="colorpicker__grid">
          {CARD_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              className={`colorpicker__swatch colorpicker__swatch--${color}`}
              onClick={() => onPick(color)}
              aria-label={color}
            >
              {color}
            </button>
          ))}
        </div>
        <button type="button" className="btn btn--ghost" onClick={onCancel}>
          {t.common.cancel}
        </button>
      </div>
    </div>
  );
}

function ChallengePrompt({
  accuserName,
  pendingDraw,
  disabled,
  t,
}: {
  accuserName: string;
  pendingDraw: number;
  disabled: boolean;
  t: Translation;
}) {
  return (
    <div className="challenge" role="alertdialog">
      <p>{fmt(t.game.challengeTitle, { name: accuserName })}</p>
      <p className="muted">
        {fmt(t.game.challengeExplain, { bluff: pendingDraw, fail: pendingDraw + 2 })}
      </p>
      <div className="challenge__actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={disabled}
          onClick={() => net.act({ type: 'challenge_wild_four', challenge: true })}
        >
          {t.game.challengeBtn}
        </button>
        <button
          type="button"
          className="btn"
          disabled={disabled}
          onClick={() => net.act({ type: 'challenge_wild_four', challenge: false })}
        >
          {fmt(t.game.acceptBtn, { count: pendingDraw })}
        </button>
      </div>
    </div>
  );
}

function GameOverPanel({ room, t }: { room: RoomView; t: Translation }) {
  const standings = [...room.players].sort((a, b) => (a.place ?? 99) - (b.place ?? 99));
  const me = room.players.find((p) => p.id === room.self?.id);
  const won = room.game.winnerId === room.self?.id;

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <h2 className="modal__title">{won ? t.game.youWin : t.game.standingsTitle}</h2>
        <ol className="standings">
          {standings.map((p) => (
            <li key={p.id} className={p.id === room.self?.id ? 'is-me' : ''}>
              <span className="standings__place">{p.place ?? '—'}</span>
              <span className="standings__name">
                {p.nickname}
                {p.isBot && <span className="tag tag--bot">{t.common.ai}</span>}
              </span>
              <span className="standings__score">
                {p.status === 'forfeited' ? t.game.forfeited : fmt(t.game.points, { points: p.score ?? 0 })}
              </span>
            </li>
          ))}
        </ol>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={() => net.leaveRoom()}>
            {t.game.backToLobby}
          </button>
          <button
            type="button"
            className={me?.ready ? 'btn' : 'btn btn--primary'}
            onClick={() => net.setReady(!me?.ready)}
          >
            {me?.ready ? t.game.waitingOthers : t.game.playAgain}
          </button>
        </div>
      </div>
    </div>
  );
}
